import { createElement } from "react";
import type { ComponentType } from "react";
import { injectStores, kebabToCamel, kebabToTitle } from "@intrasync/react";
import type { RouteType } from "types/RouteType";
import type { ServiceStoreConfig } from "types/ServiceStoreTypes";
import type RootStore from "stores/RootStore";
import LoadErrorState from "components/LoadErrorState/LoadErrorState";
import {
  findOrThrow,
  SERVICE_UNAVAILABLE_MESSAGE,
  swallowedFindMessage,
  swallowedSessionEnded,
} from "shared/loadOrError";

/**
 * Rows pulled per generated ServiceView page.
 *
 * Matches the limit `@intrasync/react`'s ServiceViewBody would have used in its
 * own `loadServiceData()` — see the `bindServiceView` note below for why that
 * method never runs and this hook does the load instead. Only enable
 * `serviceView` on reference-sized tables; there is no server-side paging on
 * this surface, the grid holds the whole result set in memory.
 */
const SERVICE_VIEW_ROW_LIMIT = 2000;

/** The slice of `@intrasync/react`'s ServiceViewStore this generator drives. */
interface ServiceViewStoreBinding {
  setCurrentService: (serviceName: string, config: ServiceStoreConfig) => void;
}

/**
 * Body loader that gives each generated route its OWN React component identity.
 *
 * Every generated route renders the same `ServiceViewBody`, and React resolves
 * lazy element types before reconciling — so navigating from one generated
 * route to another matched the resolved type, REUSED the fiber, and never
 * remounted the body. That matters because the `DataGrid` inside it binds its
 * DataGridStore namespace in its CONSTRUCTOR (`setupDataGrid`) and has no
 * componentDidUpdate: a reused instance keeps pointing at the previous
 * service's namespace. Observed live at 26.8.15 — the card title switched to
 * "Departments Management" while the grid still showed module-catalog's columns
 * and rows, and the header's search box vanished (it renders only when the
 * namespace for the CURRENT service exists).
 *
 * Wrapping in a per-service component makes the resolved types differ, so React
 * unmounts the old body and the DataGrid constructor runs again for the new
 * namespace. Remove this once DataGrid re-runs setup when `namespace` changes.
 */
function makeServiceViewBodyLoader(serviceName: string, config: ServiceStoreConfig) {
  let wrapped: ComponentType<Record<string, unknown>> | null = null;
  return async () => {
    if (!wrapped) {
      const m = await import("@intrasync/react");
      const Body = m.ServiceViewBody as ComponentType<Record<string, unknown>>;
      const title = `${kebabToTitle(serviceName)} unavailable`;
      // Failed, not empty: the library grid renders "No data available" for a
      // failed load exactly as for a genuinely empty table, so when the load
      // failed the grid is not mounted at all — the error and a Retry are.
      // Mounting the grid only after a successful retry also means its
      // constructor binds against rows that are actually there.
      const PerServiceBody = injectStores(
        ({ rootStore, ...props }: { rootStore?: RootStore } & Record<string, unknown>) => {
          const loadError = rootStore
            ? serviceViewLoadError(rootStore, serviceName)
            : null;
          if (rootStore && loadError) {
            return createElement(LoadErrorState, {
              title,
              message: loadError,
              onRetry: () => void loadServiceView(rootStore, serviceName, config),
            });
          }
          return createElement(Body, props);
        },
        { stores: ["rootStore"] }
      ) as ComponentType<Record<string, unknown>>;
      PerServiceBody.displayName = `ServiceViewBody(${serviceName})`;
      wrapped = PerServiceBody;
    }
    return { default: wrapped };
  };
}

/**
 * Why a generated ServiceView has nothing to show, or null when it loaded.
 *
 * Reads the ServiceStore's own `errorOnFind`, which the library clears at the
 * start of every find and sets when one fails (whether or not it rethrows) —
 * so it is observable, survives the route hook, and clears on a good retry.
 */
export function serviceViewLoadError(
  rootStore: RootStore,
  serviceName: string
): string | null {
  const serviceStore = (rootStore as unknown as Record<string, unknown>)[
    servicePathToStoreName(serviceName)
  ];
  if (!serviceStore || typeof serviceStore !== "object" || !("find" in serviceStore)) {
    return SERVICE_UNAVAILABLE_MESSAGE;
  }
  // A find a sign-out ended is not this view's failure: the next person would
  // read "Signed out before … loaded." as their own error state.
  if (swallowedSessionEnded(serviceStore)) return null;
  return swallowedFindMessage(serviceStore);
}

/**
 * Load a generated ServiceView's rows, then bind the ServiceView store to it.
 * The route's beforeEnter (and so the header refresh button) and the error
 * state's Retry both run this. Never rejects; returns false when the load
 * failed (the failure is then on the ServiceStore's `errorOnFind`).
 */
export async function loadServiceView(
  rootStore: RootStore,
  serviceName: string,
  config: ServiceStoreConfig
): Promise<boolean> {
  const storeName = servicePathToStoreName(serviceName);
  const serviceStore = rootStore[storeName];

  if (!serviceStore || typeof serviceStore !== "object" || !("find" in serviceStore)) {
    console.warn(`Service store ${storeName} not found or invalid.`);
    return false;
  }

  // Pre-load the service data. Must happen BEFORE the bind below: with no
  // explicit `columnHeaders` in the config, ServiceViewStore infers them
  // from `entities[0]`, and an empty cache yields zero columns (the body
  // then renders "No data available" even once the rows arrive).
  //
  // `findOrThrow`, not a bare find: the ServiceStore swallows a failed find
  // (resolves null), so the catch below never fired and the grid said
  // "No data available" over a failed load. The body now renders an error
  // state + Retry off `serviceViewLoadError`.
  let ok = true;
  try {
    await findOrThrow(serviceStore as Parameters<typeof findOrThrow>[0], {
      query: { $limit: SERVICE_VIEW_ROW_LIMIT },
    });
  } catch (err) {
    ok = false;
    console.error(`Failed to load data for ${serviceName}:`, err);
  }

  // Bind the ServiceView store to THIS service.
  //
  // Not redundant with ServiceViewBody's own effect — that effect cannot
  // work here. It resolves the config by matching the URL segment against
  // `@intrasync/react`'s OWN bundled `serviceStoresConfig`, which the
  // package ships as a hard-coded EMPTY ARRAY (dist/serviceStoresConfig.js,
  // still empty at 26.8.15). The library's generic generator expects the
  // consumer to inject its own Body component wired to the app's real
  // config; HQ uses the prebuilt `ServiceViewBody`, so the match never
  // succeeds and `setCurrentService` is never reached. Without this line
  // every generated route renders "No data available" for every service.
  // Still bound on a failed load, so the header and cached rows stay wired.
  const serviceViewStore = rootStore.serviceViewStore as
    | ServiceViewStoreBinding
    | undefined;
  if (typeof serviceViewStore?.setCurrentService === "function") {
    serviceViewStore.setCurrentService(serviceName, config);
  } else {
    console.warn(
      `serviceViewStore is not mounted — ${serviceName} cannot render.`
    );
  }

  return ok;
}

/**
 * Generates dynamic routes for service stores that have ServiceView enabled
 * Routes are created under /management/data/[service-name]
 *
 * These raw grids are admin debugging, not pages (walkthrough §4.1): they are
 * `hidden` — out of the nav and the omnibar — and reachable only by URL. Each
 * one has a hand-built page or a sync behind it, so a person navigating never
 * needs them. The header is titled "Raw data" because the library
 * `ServiceViewHeader` already prints "<Title> - Count: n/m" beside it; titling
 * the plate "<Title> Management" said the name twice.
 *
 * @param serviceStores Array of service store configurations
 * @returns Array of RouteType objects for ServiceView-enabled stores
 */
export function generateServiceViewRoutes(
  serviceStores: ServiceStoreConfig[]
): RouteType[] {
  return serviceStores
    .filter(store => store.options?.serviceView?.enableServiceView)
    .map(store => {
      const permissions = store.options?.serviceView?.permissions;
      const serviceName = store.name;
      const title = kebabToTitle(serviceName);

      return {
        name: `${title} Management`,
        path: `/management/data/${serviceName}`,
        layout: "/app",
        hidden: true,
        viewportCard: {
          header: {
            title: "Raw data",
            component: () => import("@intrasync/react").then((m) => ({ default: m.ServiceViewHeader })),
            enableRefreshButton: true,
            enableBackButton: true,
            color: "primary",
          },
          body: {
            grayBackground: true,
            component: makeServiceViewBodyLoader(serviceName, store),
          },
        },
        dialogs: [
          {
            name: "ServiceViewCsvExport",
            title: "Export to CSV",
            component: () => import("@intrasync/react").then((m) => ({ default: m.ServiceViewCsvExport })),
            cancelButton: false,
            height: 600,
          },
          {
            name: "ServiceViewPdfExport",
            title: "Export to PDF",
            component: () => import("@intrasync/react").then((m) => ({ default: m.ServiceViewPdfExport })),
            cancelButton: false,
            height: 700,
          },
          {
            name: "ServiceViewCreateEntity",
            // The library header's Add button opens this; "Create New Entity"
            // named no entity. Only services the API's api-services registry
            // gives a dataSchema (departments) can actually create here.
            title: `Add to ${title}`,
            component: () => import("@intrasync/react").then((m) => ({ default: m.ServiceViewCreateEntity })),
            cancelButton: false,
            height: 600,
          },
        ],
        group: "Management",
        requiresAuth: permissions?.requiresAuth ?? true,
        requiresAdmin: permissions?.requiresAdmin ?? true,
        requiresPermission: permissions?.requiresPermission ? true : false,
        // Also THE refresh path: RouterStore.triggerEnterSideEffects(true) — what
        // the header's refresh button calls — re-runs beforeEnter, so re-loading
        // and re-binding here is what makes that button do anything.
        beforeEnter: async ({ rootStore }: { rootStore: RootStore }) => {
          // Always enter: a failed load renders as an error state + Retry in
          // the body (serviceViewLoadError), never as a blocked navigation.
          await loadServiceView(rootStore, serviceName, store);
          return true;
        },
      };
    });
}

/**
 * Utility to convert service path to store name
 * Used in route generation and components
 * @param servicePath The service path (e.g., "purchase-orders")
 * @returns The store name (e.g., "purchaseOrdersStore")
 */
export function servicePathToStoreName(servicePath: string): string {
  return kebabToCamel(servicePath) + "Store";
}
